import apiBaseUrl from '../../utils/config.js';

class AuthService {
    constructor() {
        this.baseUrl = `${apiBaseUrl}/auth`;
    }

    // Login
    async login(loginRequest) {
        const response = await fetch(`${this.baseUrl}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(loginRequest)
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        const result = await response.json();

        if (result.token) {
            localStorage.setItem("jwt", result.token);
        }

        if (result.username) {
            localStorage.setItem("username", result.username);
        }

        if (result.role) {
            localStorage.setItem("role", this.cleanRole(result.role));
        }

        return result;
    }

    // Refresh JWT silently
    async refreshToken() {
        const token = this.getToken();

        if (!token) {
            throw new Error("No token found");
        }

        const response = await fetch(`${this.baseUrl}/refresh`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        const result = await response.json();

        if (!result.token) {
            throw new Error("Refresh response did not include a token");
        }

        localStorage.setItem("jwt", result.token);

        if (result.username) {
            localStorage.setItem("username", result.username);
        }

        if (result.role) {
            localStorage.setItem("role", this.cleanRole(result.role));
        }

        if (result.patientId !== undefined && result.patientId !== null) {
            localStorage.setItem("patientId", String(result.patientId));
        }

        return result;
    }

    // Register new user
    async register(username, password, role) {
        const response = await fetch(`${this.baseUrl}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password, role })
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        const result = await response.json();

        if (result.token) {
            localStorage.setItem("jwt", result.token);
        }

        if (result.username) {
            localStorage.setItem("username", result.username);
        }

        if (result.role) {
            localStorage.setItem("role", this.cleanRole(result.role));
        }

        return result;
    }

    // Delete a user
    async deleteUser(username) {
        const token = this.getToken();

        const response = await fetch(`${this.baseUrl}/delete/${username}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return await response.json();
    }

    // Edit user credentials
    async editUser(username, newPassword, newRole) {
        const token = this.getToken();

        const response = await fetch(`${this.baseUrl}/edit`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ username, newPassword, newRole })
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return await response.json();
    }

    // Get user role
    async getUserRole(username) {
        const token = this.getToken();

        const response = await fetch(`${this.baseUrl}/get-role/${username}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return await response.json();
    }

    // Check if logged in
    async checkAuthStatus() {
        const response = await fetch(`${this.baseUrl}/check-auth`, {
            credentials: 'include'
        });

        if (!response.ok) {
            return { authenticated: false };
        }

        return await response.json();
    }

    // Generic request with auth
    async makeAuthRequest(endpoint, method = 'GET', body = null) {
        const token = this.getToken();

        const response = await fetch(`${this.baseUrl}${endpoint}`, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            ...(body && { body: JSON.stringify(body) })
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return await response.json();
    }

    // JWT helpers
    getToken() {
        return localStorage.getItem("jwt");
    }

    getUsername() {
        return localStorage.getItem("username");
    }

    getRole() {
        return localStorage.getItem("role");
    }

    cleanRole(role) {
        if (!role) return "";
        return role.startsWith("ROLE_") ? role.replace("ROLE_", "") : role;
    }

    logout() {
        localStorage.removeItem("jwt");
        localStorage.removeItem("username");
    }
}

export default AuthService;